import React from "react";
import HistoryDataSidebar, {
  HistoryDataSidebarSummaryItem,
} from "components/shared/history-data-sidebar";
import { useCustomerHistory } from "../hooks/useCustomerHistory";
import { useAppTranslation } from "locale/useAppTranslation";
import { CustomerV1GetCustomerDetailRes } from "_services/modules/customer";
import { historyDateTimeFormatter } from "_helper/timeFormatter";
import { displayUserName } from "_helper/actor";

interface CustomerHistorySidebarProps {
  open: boolean;
  onClose: () => void;
  customerId?: string;
  detail?: CustomerV1GetCustomerDetailRes;
}

const CustomerHistorySidebar: React.FC<CustomerHistorySidebarProps> = ({
  open,
  onClose,
  customerId,
  detail,
}) => {
  const { t: tHistory } = useAppTranslation("historyDataSidebar");

  const {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    searchValue,
    setSearchValue,
    fromDateValue,
    toDateValue,
    onDateChange,
  } = useCustomerHistory({ customerId, isOpen: open });

  const summaryItems: HistoryDataSidebarSummaryItem[] = [];

  const rawCreatedName =
    typeof detail?.created_by === "object"
      ? detail.created_by?.name
      : detail?.created_by;
  const createdTime =
    typeof detail?.created_by === "object" && detail.created_by?.timestamp
      ? detail.created_by.timestamp
      : detail?.created_at;

  const formattedCreatedAt = createdTime ? historyDateTimeFormatter(createdTime) : "—";
  if (createdTime || rawCreatedName) {
    const creatorName = displayUserName(rawCreatedName);
    summaryItems.push({
      id: "created-by",
      label: tHistory("created-by") || "Created by",
      value:
        creatorName && creatorName !== "—"
          ? `${creatorName}, ${formattedCreatedAt}`
          : formattedCreatedAt,
    });
  }

  const rawUpdatedName =
    (typeof detail?.updated_by === "object" ? detail.updated_by?.name : detail?.updated_by) ||
    rawCreatedName;
  const updatedTime =
    (typeof detail?.updated_by === "object" && detail.updated_by?.timestamp
      ? detail.updated_by.timestamp
      : detail?.updated_at) ||
    createdTime;

  const formattedUpdatedAt = updatedTime ? historyDateTimeFormatter(updatedTime) : "—";
  if (updatedTime || rawUpdatedName) {
    const updaterName = displayUserName(rawUpdatedName);
    summaryItems.push({
      id: "updated-by",
      label: tHistory("last-update-by") || "Last Update by",
      value:
        updaterName && updaterName !== "—"
          ? `${updaterName}, ${formattedUpdatedAt}`
          : formattedUpdatedAt,
    });
  }

  return (
    <HistoryDataSidebar
      open={open}
      onClose={onClose}
      items={items}
      summaryItems={summaryItems}
      title={tHistory("title")}
      subtitle={tHistory("subtitle")}
      emptyTitle={isLoading ? "Loading history..." : undefined}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      fromDateValue={fromDateValue}
      toDateValue={toDateValue}
      onDateChange={onDateChange}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      onLoadMore={loadMore}
    />
  );
};

export default CustomerHistorySidebar;
